<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Test Providers"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>

              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn :to="{ name: 'add-test-provider' }" exact variant="primary"> Add Test Provider </b-btn>
              </div>
            </div>
          </div>

          <!-- Test Providers Table -->
          <b-table
            :fields="tableColumns"
            :items="testProviders"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
            </template>

            <template #cell(scope)="data">
              <b-badge v-if="data.item.is_national" variant="none" class="border border-info text-info">
                National
              </b-badge>
              <span v-else>{{ (data.item.states || []).join(', ') || '—' }}</span>
            </template>

            <template #cell(tested)="data">
              <b-badge v-if="data.item.theory_tested" variant="none" class="border border-secondary text-secondary mr-1">
                Theory
              </b-badge>
              <b-badge v-if="data.item.skill_tested" variant="none" class="border border-secondary text-secondary mr-1">
                Skill
              </b-badge>
              <b-badge
                v-if="data.item.clinical_competency_required"
                variant="none"
                class="border border-secondary text-secondary"
              >
                Clinical
              </b-badge>
            </template>

            <template #cell(is_active)="data">
              <b-badge
                :class="`border border-${data.item.is_active ? 'success' : 'danger'} text-${
                  data.item.is_active ? 'success' : 'danger'
                }`"
                variant="none"
                >{{ data.item.is_active ? 'Active' : 'Inactive' }}</b-badge
              >
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item @click="updateStatus(data.index)">
                  <md-icon :class="data.item.is_active ? 'text-danger mr-2' : 'text-success mr-2'">{{
                    data.item.is_active ? 'block' : 'check_circle_outline'
                  }}</md-icon>
                  {{ data.item.is_active ? 'Mark Inactive' : 'Mark Active' }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item
                  :to="{
                    name: 'edit-test-provider',
                    params: { id: data.item.id },
                  }"
                >
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="removeTestProvider(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalTestProviders"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="test-providers-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: { MdIcon, PageHeader, PageSeparator, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Manage Test Providers',
      searchTerm: '',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalTestProviders: 0,
      ordering: null,
      testProviders: [],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Test Providers', active: true },
      ];
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '120px' },
        },
        { key: 'name', label: this.$t('schoolMsgs.name') },
        { key: 'provider_name', label: 'Provider' },
        { key: 'program.title', label: 'Program' },
        { key: 'scope', label: 'Coverage' },
        { key: 'tested', label: 'Tested' },
        { key: 'is_active', label: this.$t('generalMsgs.status') },
      ];
    },
  },

  methods: {
    ...mapActions('testProvider', ['getAllTestProviders', 'updateTestProvider', 'deleteTestProvider']),
    get,

    async updateStatus(index) {
      this.isLoading = true;
      const currStatus = this.testProviders[index].is_active;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to mark the status ${currStatus ? 'Inactive' : 'Active'}.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.updateTestProvider({ id: this.testProviders[index].id, data: { is_active: !currStatus } });
          this.fetchTestProviders(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Status changed successfully!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async removeTestProvider(id) {
      this.isLoading = true;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected test provider.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteTestProvider(id);
          this.fetchTestProviders();
          this.makeToast({ variant: 'success', msg: 'Test provider deleted successfully!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchTestProviders(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const response = await this.getAllTestProviders({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });
      this.testProviders = response.data.results;
      this.currentPage = pageNum;
      this.totalTestProviders = response.data.count;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchTestProviders(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchTestProviders();
    },

    onSearch() {
      this.debouncedSearchTestProviders(this);
    },

    debouncedSearchTestProviders: debounce((vm) => {
      vm.fetchTestProviders();
    }, 500),
  },

  async mounted() {
    await this.fetchTestProviders();
  },
};
</script>
